import { TooltipProvider } from "@datascratch/ui";
import { Editor } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { Awareness } from "y-protocols/awareness";
import type * as Y from "yjs";
import { setEditorAwareness } from "./collab/collaboration";
import { buildExtensions } from "./editorCore";
import { alignPaper, clearPaper, PaperSnap } from "./paper";
import type { EditorHost, HostRef } from "./host";
import { readNoteMeta, readTitle, setNoteCover, SUPPORTED_FORMAT_VERSION, useNoteMeta } from "./meta";
import { PenLayer, type PenSettings } from "./pen/PenLayer";
import { acceptAll, blocksWithAgentEdits } from "./schema/agentReview";
import { guardSchema } from "./schema/unknown";
import { TASK_MENU_EVENT, type TaskMenuRequest } from "./schema/tasks";
import { fillEmptyTitleIfNeeded, focusTitle } from "./schema/title";
import { insertUpload, pickAndInsert, preparePastedImages, startPastedImages } from "./uploads";
import { normalizePastedHtml, looksLikeMarkdown, untitleSlice } from "./paste/normalize";
import { markdownToHtml } from "./paste/markdown";
import { DOMParser as PMDOMParser, type Slice } from "@tiptap/pm/model";
import type { NoteStyle, StrokeAuthor } from "@datascratch/shared/contracts";
import { filterBlocks, type BlockItem } from "./ui/blockItems";
import { BubbleToolbar } from "./ui/BubbleToolbar";
import { BlockLift } from "./ui/blockLift";
import { DragHandle } from "./ui/DragHandle";
import { PenPalette } from "./ui/PenPalette";
import { NoteLinkMenu, SlashMenu, type LinkItem } from "./ui/Popups";
import { createSuggestionExtension, createSuggestionStore } from "./ui/suggestions";
import { TaskMenu } from "./ui/TaskMenu";

export interface EditorUser {
  id: string;
  name: string;
  /** Cursor colour for collaborators (any CSS colour). */
  color: string;
  kind?: StrokeAuthor["kind"];
}

export interface EditorApi {
  editor: Editor;
  pen: PenLayer;
  undoManager: Y.UndoManager;
  /** Caret at the end of the title, editor focused (a new note). */
  focusTitle(): void;
  /** Fills an empty title with `host.defaultTitle()` (call when the note closes); true if it did. */
  fillEmptyTitleIfNeeded(): boolean;
  /** Opens the image picker and inserts the picked images at the caret (the palette's picture button). */
  pickImage(): void;
  /** Inserts files at the caret right away and uploads them behind a placeholder. */
  insertFiles(files: File[]): void;
}

export interface DatascratchEditorProps {
  /** The note document (document-model.md §4). */
  doc: Y.Doc;
  /** Presence of collaborators (cursors, selections). */
  awareness?: Awareness | null;
  user?: EditorUser;
  editable?: boolean;
  /** Callbacks into the app: attachments, note links, page cards, tags, task actions. */
  host?: EditorHost;
  /** Overrides `meta.style` of the document (e.g. while previewing a style picker). */
  styleOverride?: Partial<NoteStyle>;
  /** Show the cover from `meta.cover` above the title. */
  showCover?: boolean;
  /** Called when the user asks to pick a cover image (the app shows its picker and calls setNoteCover). */
  onRequestCover?: () => void;
  showPalette?: boolean;
  penSettings?: Partial<PenSettings>;
  /**
   * D-45: `NoteMeta.createdByAgent` of this note. Shows the strip «Создано агентом {agentName}» with
   * «Понятно»; the app clears the field in `onAcknowledgeAgentCreated`.
   */
  createdByAgent?: { agentId: string; agentName: string; at: string } | null;
  onAcknowledgeAgentCreated?: () => void;
  /** Plain-text title, after each change (the app copies it into NoteMeta.title). */
  onTitleChange?: (title: string) => void;
  onReady?: (api: EditorApi) => void;
  className?: string;
  /** Extra vertical space under the last block (px), so the palette never covers text. */
  bottomPadding?: number;
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

const FONT_VARS: Record<NoteStyle["font"], string> = {
  serif: "var(--ds-font-display, 'Newsreader', 'New York', Georgia, serif)",
  sans: "var(--ds-font-text, 'Inter', system-ui, sans-serif)",
  mono: "var(--ds-font-mono, 'JetBrains Mono', ui-monospace, monospace)",
};
const WIDTHS: Record<NoteStyle["width"], string> = { normal: "720px", wide: "960px", full: "100%" };

function useHostRef(host: EditorHost | undefined): HostRef {
  const ref = useRef<EditorHost>(host ?? {});
  ref.current = host ?? {};
  return ref;
}

/**
 * The note editor: TipTap over `Y.XmlFragment "content"` + the pen layer over `Y.Map "strokes"`.
 * Everything is stored in the Y.Doc; the component never owns document state.
 */
export function DatascratchEditor(props: DatascratchEditorProps) {
  const { doc, awareness = null, user, editable = true, showPalette = true, showCover = true, className = "", bottomPadding = 160 } = props;
  const taskDates = (props.host?.taskDates ?? "full") === "full";
  const host = useHostRef(props.host);
  const meta = useNoteMeta(doc);
  const readOnly = !editable || meta.formatVersion > SUPPORTED_FORMAT_VERSION;
  const rootRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const [api, setApi] = useState<EditorApi | null>(null);
  const [schemaGen, setSchemaGen] = useState(0);
  const [taskMenu, setTaskMenu] = useState<TaskMenuRequest | null>(null);
  const [agentBlocks, setAgentBlocks] = useState(0);
  const slashStore = useMemo(() => createSuggestionStore<BlockItem>(), []);
  const linkStore = useMemo(() => createSuggestionStore<LinkItem>(), []);
  const awarenessRef = useRef(awareness);
  /** Which awareness each editor instance was built or last updated with. */
  const attachedAwareness = useRef(new WeakMap<Editor, Awareness | null>());
  const callbacks = useRef(props);
  callbacks.current = props;

  useEffect(() => {
    const mount = mountRef.current;
    const root = rootRef.current;
    if (!mount || !root) return;
    const slash = createSuggestionExtension<BlockItem>({
      name: "dsSlash",
      char: "/",
      store: slashStore,
      allow: ({ state, range }) => {
        const $from = state.doc.resolve(range.from);
        return $from.parent.type.name !== "title" && !$from.parent.type.spec.code;
      },
      items: (q) => filterBlocks(q, host.current).map((b) => (b.id === "task" && host.current.taskDates === "inert" ? { ...b, hint: "Чек-бокс" } : b)),
      command: ({ editor, range, item }) => item.run(editor, range, host.current),
    });
    const links = createSuggestionExtension<LinkItem>({
      name: "dsNoteLinks",
      char: "[[",
      allowSpaces: true,
      store: linkStore,
      // A closed `[[Title]]` (typed or written by an agent) is a link, not an open query.
      allow: ({ state, range }) => !state.doc.textBetween(range.from, range.to).includes("]]"),
      items: async (q) => {
        const found = (await host.current.searchNotes?.(q.replace(/\]+$/, ""))) ?? [];
        const items: LinkItem[] = found.slice(0, 8);
        if (q.trim() && host.current.createNote && !found.some((n) => n.title.toLowerCase() === q.trim().toLowerCase())) {
          items.push({ noteId: "", title: q.trim(), create: true });
        }
        return items;
      },
      command: ({ editor, range, item }) => {
        const insert = (noteId: string, label: string) =>
          editor
            .chain()
            .focus()
            .insertContentAt(range, [{ type: "noteLink", attrs: { noteId, label } }, { type: "text", text: " " }])
            .run();
        if (item.create) void host.current.createNote?.(item.title).then((n) => insert(n.noteId, n.title));
        else insert(item.noteId, item.title);
      },
    });
    const { extensions, undoManager } = buildExtensions({
      doc,
      host,
      awareness: awarenessRef.current,
      user: user ? { name: user.name, color: user.color } : null,
      extensions: [slash, links, PaperSnap, BlockLift],
    });
    let remountQueued = false;
    let lastTitle = readTitle(doc);
    const editor = new Editor({
      element: mount,
      extensions,
      editable: !readOnly,
      // Markdown in pasted text is parsed by clipboardTextParser (and skipped on Shift+paste); the
      // marks' own paste rules would bold `**x**` even in a plain paste. Links still autolink.
      enablePasteRules: ["link"],
      editorProps: {
        attributes: { class: "ds-prose", spellcheck: "true", "aria-label": "Текст заметки" },
        // Rich paste (spec/04 «Вставка»): HTML is tidied (normalize.ts), Markdown text goes through
        // the same path, the title takes plain text only, Shift+paste is plain text (ProseMirror).
        handlePaste: (view, event) => {
          const data = event.clipboardData;
          if (!data) return false;
          const { $from } = view.state.selection;
          if ($from.depth >= 1 && $from.node(1).type.name === "title") {
            const text = data.getData("text/plain").replace(/\s*\n\s*/g, " ").trim();
            if (text) view.dispatch(view.state.tr.insertText(text).scrollIntoView());
            return true;
          }
          // A screenshot or copied image (no rich text around it) goes in as a file.
          const html = data.getData("text/html");
          const onlyImages = !html || !new DOMParser().parseFromString(html, "text/html").body.textContent?.trim();
          if (data.files?.length && onlyImages) return uploadFiles(data.files);
          return false;
        },
        transformPasted: (slice, view) => untitleSlice(slice, view.state.schema),
        transformPastedHTML: (html) => {
          const out = normalizePastedHtml(html);
          preparePastedImages(out.images);
          return out.html;
        },
        clipboardTextParser: (text, $context, plain, view) => {
          if (plain || !looksLikeMarkdown(text)) return null as unknown as Slice;
          const out = normalizePastedHtml(markdownToHtml(text), { markdown: true });
          preparePastedImages(out.images);
          // Parsed in an inert document: nothing in it loads or runs (never the live page).
          const box = document.implementation.createHTMLDocument("").createElement("div");
          box.innerHTML = out.html;
          return PMDOMParser.fromSchema(view.state.schema).parseSlice(box, { context: $context });
        },
        handleDrop: (view, event, _slice, moved) => {
          if (moved) return false;
          const files = event.dataTransfer?.files;
          if (!files?.length || !host.current.uploadAttachment) return false;
          const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
          if (at) view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(at.pos))));
          return uploadFiles(files);
        },
      },
      onBeforeCreate: ({ editor: e }) =>
        guardSchema(e.schema, () => {
          // A newer client wrote a node/mark we don't know: rebuild the schema so it round-trips exactly.
          if (remountQueued) return;
          remountQueued = true;
          setTimeout(() => setSchemaGen((g) => g + 1), 0);
        }),
      onTransaction: ({ transaction }) => {
        // Placeholders of pasted images start their upload once the paste has landed.
        if (transaction.getMeta("paste")) queueMicrotask(() => startPastedImages(editor, host));
      },
      onUpdate: ({ editor: e }) => {
        const title = e.state.doc.firstChild?.type.name === "title" ? e.state.doc.firstChild.textContent : readTitle(doc);
        if (title !== lastTitle) {
          lastTitle = title;
          callbacks.current.onTitleChange?.(title);
        }
      },
    });
    function uploadFiles(files: FileList | null | undefined): boolean {
      const upload = host.current.uploadAttachment;
      if (!files?.length || !upload) return false;
      // The block appears at once with a local preview; the upload runs behind it (uploads.ts).
      for (const file of Array.from(files)) insertUpload(editor, host, file);
      return true;
    }
    const pen = new PenLayer({
      editor,
      doc,
      root,
      undoManager,
      author: { kind: user?.kind ?? "user", id: user?.id ?? "" },
      settings: callbacks.current.penSettings,
    });
    const onTaskMenu = (e: Event) => setTaskMenu((e as CustomEvent<TaskMenuRequest>).detail);
    editor.view.dom.addEventListener(TASK_MENU_EVENT, onTaskMenu);
    attachedAwareness.current.set(editor, awarenessRef.current);
    const next: EditorApi = {
      editor,
      pen,
      undoManager,
      focusTitle: () => focusTitle(editor),
      fillEmptyTitleIfNeeded: () => fillEmptyTitleIfNeeded(editor, host, undoManager),
      pickImage: () => pickAndInsert(editor, host, "image/*"),
      insertFiles: (files) => {
        for (const f of files) insertUpload(editor, host, f);
      },
    };
    setApi(next);
    callbacks.current.onReady?.(next);
    return () => {
      editor.view.dom.removeEventListener(TASK_MENU_EVENT, onTaskMenu);
      pen.destroy();
      editor.destroy();
      setApi(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `awareness` is applied to the live editor below; it must not remount it.
  }, [doc, schemaGen]);

  // Presence source changed (new provider, reconnect): swap the cursor plugin on the live editor.
  useEffect(() => {
    awarenessRef.current = awareness;
    if (!api || attachedAwareness.current.get(api.editor) === awareness) return;
    setEditorAwareness(api.editor, awareness, user ? { name: user.name, color: user.color } : null);
    attachedAwareness.current.set(api.editor, awareness);
  }, [api, awareness]);

  useEffect(() => {
    api?.editor.setEditable(!readOnly);
  }, [api, readOnly]);

  // Agent edits pending review (D-10): drives the "Правки агента · Принять все" banner.
  useEffect(() => {
    if (!api) return;
    const { editor } = api;
    let frame = 0;
    const count = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => !editor.isDestroyed && setAgentBlocks(blocksWithAgentEdits(editor.state.doc).length));
    };
    count();
    editor.on("update", count);
    editor.on("create", count);
    const onTr = ({ transaction }: { transaction: { docChanged: boolean } }) => transaction.docChanged && count();
    editor.on("transaction", onTr);
    return () => {
      cancelAnimationFrame(frame);
      editor.off("update", count);
      editor.off("create", count);
      editor.off("transaction", onTr);
    };
  }, [api]);

  useEffect(() => {
    if (api && props.penSettings) api.pen.setSettings(props.penSettings);
  }, [api, props.penSettings]);

  useEffect(() => {
    if (!awareness || !user) return;
    awareness.setLocalStateField("user", { name: user.name, color: user.color });
  }, [awareness, user?.name, user?.color]);

  const style = { ...meta.style, ...props.styleOverride };
  useEffect(() => {
    // Fonts and widths change line breaks: strokes must follow (D-05).
    api?.pen.invalidate();
  }, [api, style.font, style.width, style.paper]);

  // Ruled paper: keep the rules under the text after any relayout (paper.ts).
  const ruled = style.paper !== "plain";
  const editorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = editorRef.current;
    const root = rootRef.current;
    if (!api || !el || !root) return;
    const { editor } = api;
    if (!ruled) {
      clearPaper(editor.view, el);
      return;
    }
    let frame = 0;
    const align = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => !editor.isDestroyed && alignPaper(editor.view, el, root));
    };
    align();
    const ro = new ResizeObserver(align);
    ro.observe(editor.view.dom);
    ro.observe(root);
    editor.on("update", align);
    root.addEventListener("ds-layout", align);
    document.fonts?.addEventListener("loadingdone", align);
    void document.fonts?.ready.then(align);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      editor.off("update", align);
      root.removeEventListener("ds-layout", align);
      document.fonts?.removeEventListener("loadingdone", align);
    };
  }, [api, ruled, style.font, style.width, style.paper]);

  const cssVars = {
    "--ds-doc-font": FONT_VARS[style.font],
    "--ds-doc-width": WIDTHS[style.width],
    "--ds-doc-bottom": `${bottomPadding}px`,
    ...(style.textColor ? { "--ds-doc-text": `var(--ds-${style.textColor.replace(/\./g, "-")})` } : {}),
  } as CSSProperties;

  return (
    // Design-system popovers use Radix tooltips; a local provider keeps the editor usable without UIProvider.
    <TooltipProvider delayDuration={500} skipDelayDuration={200}>
    <div ref={editorRef} className={`ds-editor ${className}`} data-paper={style.paper} data-ruled={ruled || undefined} data-font={style.font} data-width={style.width} data-readonly={readOnly || undefined} data-task-dates={taskDates ? undefined : "off"} style={cssVars}>
      {showCover && meta.cover && <Cover doc={doc} host={host.current} editable={!readOnly} onRequestCover={props.onRequestCover} />}
      {meta.formatVersion > SUPPORTED_FORMAT_VERSION && (
        <div className="ds-readonly-banner" role="status">
          Заметка создана в более новой версии приложения и открыта только для чтения. Обновите приложение, чтобы редактировать.
        </div>
      )}
      {props.createdByAgent && (
        <div className="ds-agent-banner ds-agent-created" role="status">
          <span className="ds-agent-banner-dot" aria-hidden="true" />
          <span className="ds-agent-banner-text">Создано агентом {props.createdByAgent.agentName}</span>
          {props.onAcknowledgeAgentCreated && (
            <button type="button" className="ds-agent-banner-accept" onClick={props.onAcknowledgeAgentCreated}>
              Понятно
            </button>
          )}
        </div>
      )}
      {api && agentBlocks > 0 && !readOnly && (
        <div className="ds-agent-banner" role="status">
          <span className="ds-agent-banner-dot" aria-hidden="true" />
          <span className="ds-agent-banner-text">
            Правки агента · {agentBlocks} {plural(agentBlocks, "блок", "блока", "блоков")}
          </span>
          <button
            type="button"
            className="ds-agent-banner-accept"
            onClick={() => {
              const tr = acceptAll(api.editor.state);
              if (tr) api.editor.view.dispatch(tr);
            }}
          >
            Принять все
          </button>
        </div>
      )}
      <div ref={rootRef} className="ds-editor-root">
        <div ref={mountRef} className="ds-editor-mount" />
        {api && <DragHandle editor={api.editor} root={rootRef.current!} />}
      </div>
      {api && <BubbleToolbar editor={api.editor} />}
      <SlashMenu store={slashStore} />
      <NoteLinkMenu store={linkStore} />
      {api && taskMenu && <TaskMenu editor={api.editor} request={taskMenu} host={host.current} dates={taskDates} onClose={() => setTaskMenu(null)} />}
      {api && showPalette && !readOnly && (
        <div className="ds-palette-dock">
          <PenPalette pen={api.pen} undoManager={api.undoManager} onInsertImage={host.current.uploadAttachment && !readOnly ? () => api.pickImage() : undefined} />
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}

/** Document cover (spec/12 §12.3): image from `meta.cover` with a draggable vertical offset. */
function Cover({ doc, host, editable, onRequestCover }: { doc: Y.Doc; host: EditorHost; editable: boolean; onRequestCover?: () => void }) {
  const meta = useNoteMeta(doc);
  const cover = meta.cover;
  const [url, setUrl] = useState<string | null>(null);
  const [offset, setOffset] = useState(cover?.offsetY ?? 0.5);
  const [dragging, setDragging] = useState(false);
  useEffect(() => setOffset(cover?.offsetY ?? 0.5), [cover?.offsetY]);
  useEffect(() => {
    let alive = true;
    setUrl(null);
    if (cover?.attachmentId) void host.resolveAttachment?.(cover.attachmentId).then((u) => alive && setUrl(u));
    return () => {
      alive = false;
    };
  }, [cover?.attachmentId, host]);
  if (!cover) return null;
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!editable || !dragging) return;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const startY = e.clientY;
    const start = offset;
    const h = el.getBoundingClientRect().height;
    const move = (ev: PointerEvent) => setOffset(Math.min(1, Math.max(0, start - (ev.clientY - startY) / (h * 2))));
    const up = (ev: PointerEvent) => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      const next = Math.min(1, Math.max(0, start - (ev.clientY - startY) / (h * 2)));
      setNoteCover(doc, { ...readNoteMeta(doc).cover!, offsetY: Math.round(next * 1000) / 1000 });
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
  };
  return (
    <div className={`ds-cover${dragging ? " is-repositioning" : ""}`} style={{ backgroundImage: url ? `url("${url}")` : undefined, backgroundPosition: `center ${offset * 100}%` }} onPointerDown={onPointerDown}>
      {editable && (
        <div className="ds-cover-actions ds-glass">
          {onRequestCover && (
            <button type="button" onClick={onRequestCover}>
              Сменить
            </button>
          )}
          <button type="button" onClick={() => setDragging((d) => !d)}>
            {dragging ? "Готово" : "Сдвинуть"}
          </button>
          <button type="button" onClick={() => setNoteCover(doc, null)}>
            Убрать
          </button>
        </div>
      )}
    </div>
  );
}
